import React, { useEffect, useId, useState } from "react";
import { isOemSpecificColor, matchStandardColor } from "../constants/vehicleColors";

export interface VinColorFieldProps {
  id?: string;
  label: string;
  value: string;
  /** Sticky OEM / VIN-suggested color (manufacturer name), if any. */
  suggestedColor?: string | null;
  /** Badge text on the OEM suggestion card. */
  suggestionBadge?: string;
  options: readonly string[];
  onChange: (value: string) => void;
  required?: boolean;
  darkMode?: boolean;
}

/**
 * Color picker that keeps manufacturer VIN names out of the generic dropdown.
 *
 * - No VIN suggestion → standard select only.
 * - VIN suggestion matches a standard color → select with that value.
 * - VIN suggestion is OEM-specific → show it as an accepted suggestion card;
 *   "Choose a different color" reveals the generic list; seller can restore
 *   the VIN color without mixing the two option sets.
 */
const VinColorField: React.FC<VinColorFieldProps> = ({
  id: idProp,
  label,
  value,
  suggestedColor,
  suggestionBadge = "From VIN",
  options,
  onChange,
  required = true,
  darkMode = false,
}) => {
  const autoId = useId();
  const id = idProp || autoId;
  const oemSuggested =
    suggestedColor && isOemSpecificColor(options, suggestedColor)
      ? suggestedColor.trim()
      : null;
  const usingOemSuggestion = Boolean(oemSuggested && value === oemSuggested);
  const standardMatch = matchStandardColor(options, value);

  // When an OEM suggestion is accepted, hide the generic list until the seller
  // explicitly chooses to override. If they already picked a standard color
  // (or cleared), start in override mode so the select is visible.
  const [overriding, setOverriding] = useState(!usingOemSuggestion && Boolean(oemSuggested));

  useEffect(() => {
    if (!oemSuggested) {
      setOverriding(false);
      return;
    }
    if (value === oemSuggested) {
      setOverriding(false);
    } else if (value) {
      setOverriding(true);
    }
  }, [oemSuggested, value]);

  const showSuggestionCard = Boolean(oemSuggested && usingOemSuggestion && !overriding);
  const showSelect = !showSuggestionCard;

  return (
    <div className="wizard-form-group vin-color-field">
      <label htmlFor={showSelect ? id : undefined}>
        {label}
        {required ? " *" : ""}
      </label>

      {showSuggestionCard && oemSuggested && (
        <div
          className="vin-color-suggestion"
          data-testid={`${id}-vin-suggestion`}
          role="group"
          aria-label={`${label}: ${oemSuggested}`}
        >
          <div className="vin-color-suggestion-value">{oemSuggested}</div>
          <div
            className="vin-color-suggestion-badge"
            style={{
              background: darkMode ? "#1b3a26" : "#e6f4ea",
              color: darkMode ? "#7fd99a" : "#1e7a34",
            }}
          >
            {suggestionBadge}
          </div>
          <button
            type="button"
            className="vin-color-action"
            onClick={() => {
              setOverriding(true);
              // Clear so the seller must pick a standard color (or restore VIN).
              onChange("");
            }}
          >
            Choose a different color
          </button>
        </div>
      )}

      {showSelect && (
        <>
          <select
            id={id}
            className="wizard-select"
            value={standardMatch || (options.includes(value) ? value : "")}
            onChange={(e) => onChange(e.target.value)}
            required={required}
          >
            <option value="">Select color</option>
            {options.map((color) => (
              <option key={color} value={color}>
                {color}
              </option>
            ))}
          </select>
          {oemSuggested && value !== oemSuggested && (
            <button
              type="button"
              className="vin-color-action"
              onClick={() => {
                onChange(oemSuggested);
                setOverriding(false);
              }}
            >
              Use {oemSuggested}
            </button>
          )}
        </>
      )}

      {/* Keep a hidden input so form semantics / a11y still expose the OEM value
          when the visible select is replaced by the suggestion card. */}
      {showSuggestionCard && (
        <input type="hidden" id={id} name={id} value={oemSuggested || ""} readOnly />
      )}
    </div>
  );
};

export default VinColorField;
